import { useQuery } from "@apollo/client";
import { Handle, Position } from "@xyflow/react";
import { format } from "date-fns";
import { memo } from "react";
import { Link } from "react-router";
import { GetPersonById } from "../../../api/people";
import Image from "../../../components/core/Image/Image";
import Loader from "../../../components/core/Loader/Loader";
import { parseDate } from "../../../utils/date";
import styles from "./Person.module.scss";

interface FamilyTreePersonProps {
  id: string;
  isConnectable: boolean;
  data: {
    hideChildren?: boolean;
    hideParents?: boolean;
  };
  onShowParents: (fatherId: string, motherId: string) => void;
  onShowChildren: (children: string[]) => void;
}

const FamilyTreePerson = memo(
  ({
    isConnectable,
    id,
    data,
    onShowParents,
    onShowChildren,
  }: FamilyTreePersonProps) => {
    const { data: person, loading } = useQuery(GetPersonById, {
      skip: !id,
      variables: {
        where: { id },
        hydrateRelationships: true,
      },
    });

    const { hideChildren, hideParents } = data;

    const hasParents = !!person?.person?.father || !!person?.person?.mother;
    const hasChildren =
      !!person?.person?.fatherOf?.length || !!person?.person?.motherOf?.length;

    return (
      <div className={styles.familyTreePerson}>
        <Handle
          type="target"
          position={Position.Top}
          isConnectable={isConnectable}
          style={{
            opacity: 0,
            visibility: "hidden",
          }}
        />
        <Handle
          type="source"
          position={Position.Bottom}
          id="a"
          isConnectable={isConnectable}
          style={{
            opacity: 0,
            visibility: "hidden",
          }}
        />
        {loading && <Loader />}
        {!loading && (
          <>
            {hasChildren && !hideChildren && (
              <button
                className={styles.showChildren}
                type="button"
                onClick={() => {
                  onShowChildren([
                    ...(person.person?.fatherOf?.map((x) => x.id) ?? []),
                    ...(person.person?.motherOf?.map((x) => x.id) ?? []),
                  ]);
                }}
              >
                <span />
              </button>
            )}
            {hasParents && !hideParents && (
              <button
                className={styles.showParents}
                type="button"
                onClick={() => {
                  onShowParents(
                    person.person?.father?.id ?? "",
                    person.person?.mother?.id ?? "",
                  );
                }}
              >
                <span />
              </button>
            )}
            <div className={styles.image}>
              <Image
                src={
                  person?.person?.images?.[0]?.images?.map(
                    (x) => x.bild?.url ?? "",
                  )[0] ?? ""
                }
                className={styles.image}
              />
            </div>
            <div className={styles.content}>
              <div className={styles.name}>
                <Link
                  to={`/browse/people/${id}`}
                >{`${person?.person?.givenName} ${person?.person?.familyName}`}</Link>
              </div>
              <div className={styles.occupation}>
                {person?.person?.occupation
                  ?.map((x) =>
                    person.person?.sex === "female" ? x.weiblich : x.name,
                  )
                  .join(", ") ?? ""}
              </div>
              <div className={styles.birthdate}>{`${
                person?.person?.dateOfBirth
                  ? format(parseDate(person.person.dateOfBirth), "dd.MM.yyyy")
                  : ""
              } ${person?.person?.dateOfDeath ? ` — ${format(parseDate(person.person.dateOfDeath), "dd.MM.yyyy")}` : ""}`}</div>
            </div>
          </>
        )}
      </div>
    );
  },
);

FamilyTreePerson.displayName = "FamilyTreePerson";

export default FamilyTreePerson;
